import { insightsIntl } from "./intl";
import { metricTooltip } from "./metric-messages";
import type { LegacyTableColumn } from "@wayflyer/flyui-dataviz";

import { MetricTypes } from "../autogen/metrics";

export const marketingColumnTooltips = (columnId: LegacyTableColumn["id"]) => {
  return {
    Spend: "Marketing expense is comprised of those costs incurred to present goods to prospective customers",
    ROAS: "Total revenue attributed to your ad divided by total amount spent on ad (Revenue/Spend)",
    percent: "Percentage of total spend for this segment",
    "Cost Per Conversion": "Average amount spent to generate a purchase attributed to your ad (Spend/Purchases)",
    Conversions: "Number of purchases attributed to your ad",
    Impressions: "How many times your ad was displayed on a screen",
    Reach: "Number of unique individuals who saw your ad",
    Clicks: "All clicks on your ad",
    Frequency: "Average number of times a unique individual sees your ad (Impressions/Reach)",
    Audience: "Audience being targeted by your ads",
    "Audience Type": "Type of audience being targeted by your ads",
    "Attributed Conversions": "Number of purchases attributed to this ad campaign",
    "Attributed ROAS": "Return on ad spend for this campaign",
    "Attributed Conversion Value": "Total revenue attributed to this ad campaign",
    [MetricTypes.AD_SPEND]: metricTooltip(insightsIntl, MetricTypes.AD_SPEND),
    [MetricTypes.AD_IMPRESSIONS]: metricTooltip(insightsIntl, MetricTypes.AD_IMPRESSIONS),
    [MetricTypes.AD_REACH]: metricTooltip(insightsIntl, MetricTypes.AD_SPEND),
    [MetricTypes.AD_CLICKS]: metricTooltip(insightsIntl, MetricTypes.AD_SPEND),
    [MetricTypes.AD_FREQUENCY]: metricTooltip(insightsIntl, MetricTypes.AD_SPEND),
    [MetricTypes.AD_ATTRIBUTED_CONVERSION_COUNT]: metricTooltip(
      insightsIntl,
      MetricTypes.AD_ATTRIBUTED_CONVERSION_COUNT
    ),
    [MetricTypes.META_AD_SPEND]: metricTooltip(insightsIntl, MetricTypes.AD_SPEND),
    [MetricTypes.META_AD_IMPRESSIONS]: metricTooltip(insightsIntl, MetricTypes.AD_IMPRESSIONS),
    [MetricTypes.META_AD_REACH]: metricTooltip(insightsIntl, MetricTypes.AD_REACH),
    [MetricTypes.META_AD_CLICKS]: metricTooltip(insightsIntl, MetricTypes.AD_CLICKS),
    [MetricTypes.META_AD_FREQUENCY]: metricTooltip(insightsIntl, MetricTypes.AD_FREQUENCY),
    [MetricTypes.META_AD_ATTRIBUTED_CONVERSION_COUNT]: metricTooltip(
      insightsIntl,
      MetricTypes.AD_ATTRIBUTED_CONVERSION_COUNT
    ),
    [MetricTypes.META_AD_ATTRIBUTED_CONVERSION_VALUE]: metricTooltip(
      insightsIntl,
      MetricTypes.AD_ATTRIBUTED_CONVERSION_VALUE
    ),
    [MetricTypes.GOOGLE_AD_SPEND]: metricTooltip(insightsIntl, MetricTypes.AD_SPEND),
    [MetricTypes.GOOGLE_AD_IMPRESSIONS]: metricTooltip(insightsIntl, MetricTypes.AD_IMPRESSIONS),
    [MetricTypes.GOOGLE_AD_REACH]: metricTooltip(insightsIntl, MetricTypes.AD_REACH),
    [MetricTypes.GOOGLE_AD_CLICKS]: metricTooltip(insightsIntl, MetricTypes.AD_CLICKS),
    [MetricTypes.GOOGLE_AD_FREQUENCY]: metricTooltip(insightsIntl, MetricTypes.AD_FREQUENCY),
    [MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_COUNT]: metricTooltip(
      insightsIntl,
      MetricTypes.AD_ATTRIBUTED_CONVERSION_COUNT
    ),
    [MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_VALUE]: metricTooltip(
      insightsIntl,
      MetricTypes.AD_ATTRIBUTED_CONVERSION_VALUE
    ),
    [MetricTypes.TIKTOK_AD_SPEND]: metricTooltip(insightsIntl, MetricTypes.AD_SPEND),
    // Derived metrics
    [MetricTypes.PERCENTAGE_OF_TOTAL_AD_SPEND]: metricTooltip(insightsIntl, MetricTypes.PERCENTAGE_OF_TOTAL_AD_SPEND),
    [MetricTypes.AD_ATTRIBUTED_CONVERSION_ROAS]: metricTooltip(insightsIntl, MetricTypes.AD_ATTRIBUTED_CONVERSION_ROAS),
    [MetricTypes.META_AD_ATTRIBUTED_CONVERSION_ROAS]: metricTooltip(
      insightsIntl,
      MetricTypes.AD_ATTRIBUTED_CONVERSION_ROAS
    ),
    [MetricTypes.META_AD_COST_PER_CONVERSION]: metricTooltip(insightsIntl, MetricTypes.AD_COST_PER_CONVERSION),
    [MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_ROAS]: metricTooltip(
      insightsIntl,
      MetricTypes.AD_ATTRIBUTED_CONVERSION_ROAS
    ),
    [MetricTypes.GOOGLE_AD_COST_PER_CONVERSION]: metricTooltip(insightsIntl, MetricTypes.AD_COST_PER_CONVERSION),
  }[columnId];
};

export const ltvCacTableColumnTooltips = (columnId: LegacyTableColumn["id"]) => {
  return {
    fcp: "The month of the first purchase for the reviewed cohort of customer.",
    Customers: "Number of new customers acquired in a given month.",
    CAC: "Total Marketing spend divided by a number of new customers acquired this month.",
    AOV: "Average initial order value for customers in cohort",
    // Explicit metrics
    // [MetricTypes.FCP]: "The month of the first purchase for the reviewed cohort of customer.",
    [MetricTypes.ALLTIME_UNIQUE_CUSTOMER_COUNT]: "Number of new customers acquired in a given month.",
    [MetricTypes.ALLTIME_CAC]: "Total Marketing spend divided by a number of new customers acquired this month.",
    [MetricTypes.ALLTIME_AOV]: "Average initial order value for customers in cohort",
  }[columnId];
};

export const audienceDataTooltip = (record, columnId) => {
  return {
    Broad: "Targeting of individuals based on age, location etc.",
    Interest: "Targeting of individuals based on their interests e.g cars, hiking etc.",
    Lookalike: "Targeting of individuals who are similar to the Meta Ads target audiences you’ve already identified",
    "Onsite Engagers":
      "Targeting of individuals who have engaged with your brand on Facebook/Instagram e.g video view, post like etc.",
    "Offsite Engagers":
      "Targeting of individuals who engaged with your brand outside of Facebook/Instagram e.g website visitors ect.",
    "Mixed Audience": "Adset containing more than one of the above audiences",
    Custom: "Targeting of a custom list of existing customers",
    Other: "Audience that can’t be classified within any of the other definitions",
  }[record[columnId]];
};

const prospecting = `
Ads targeting cold audiences, i.e. individuals who have not interacted with your brand in the past.

Audiences included in this category are broad, interest and lookalike.
`;

const retargeting = `
Ads targeting hot audiences, i.e. individuals who have interacted with your brand in the past through social media activity, website visits etc.

Audiences included in this category are custom, onsite and offsite engagers.
`;

export const audienceCategoryDataTooltip = (record, columnId) => {
  return {
    Prospecting: prospecting,
    Retargeting: retargeting,
    "Mixed Audience": "Ads targeting a mixture of both cold and hot audiences.",
  }[record[columnId]];
};

export const channelsDataTooltipMap = {
  Summary: "An overview of all the data - not broken down by channel",
  "Marketing - Affiliates": "Revenue attributed to affiliate marketing programs",
  "Direct Traffic":
    "Revenue attributed to visitors that arrived on your site either by typing your website URL into a browser or through browser bookmarks",
  "Marketing - Google Display": "Revenue attributed to Google Display ads",
  "Marketing - Email": "Revenue attributed to email marketing campaigns",
  "Organic - Search":
    "Revenue attributed to any traffic that comes to your site from a search engine, but that isn't paid for",
  "Organic - Social Media":
    "Revenue attributed to any traffic that comes to your site from a social media platform, but that isn't paid for",
  "Other channels":
    "Revenue attributed to web traffic that has an acquisition source or medium that is not recognized within Google's default system defined channel rules",
  "Marketing - Google Branded Search": "Revenue attributed to Google Branded Search ads",
  "Marketing - Google Video": "Revenue attributed to Google Video ads",
  "Marketing - Google Search Other": "Revenue attributed to Google  Search ads not using branded or generic keywords",
  "Marketing - Google Generic Keywords Search": "Revenue attributed to Google generic keyword ads",
  "Marketing - Google Shopping": "Revenue attributed to Google shopping ads",
  "Marketing - Social Media": "Revenue attributed to Social media ads",
  "Marketing - Referral": "Revenue attributed to referral marketing programs",
  "Shopify - Other": "Shopify transactions not captured in GA and are not attributed to draft order, POS or recharge",
  // "Shopify - API integration":"",
  "Marketing - SMS": "Revenue attributed to SMS marketing campaigns",
  "Shopify - Draft Order": "Shopify wholesale transactions",
  "Shopify - Recharge": "Shopify subscription revenue captured by the recharge app",
  "Shopify - POS": "Physical store revenue",
};

export const channelMapping = {
  Affiliates: "Marketing - Affiliates",
  Direct: "Direct Traffic",
  Display: "Marketing - Google Display",
  Email: "Marketing - Email",
  "Organic Search": "Organic - Search",
  "Organic Social": "Organic - Social Media",
  Other: "Other channels",
  "Paid Search - Branded": "Marketing - Google Branded Search",
  "Paid Search - Video": "Marketing - Google Video",
  "Paid Search - Unknown": "Marketing - Google Search Other",
  "Paid Search - Generic": "Marketing - Google Generic Keywords Search",
  "Paid Search - Shopping": "Marketing - Google Shopping",
  "Performance Max": "Marketing - Google Performance Max Campaign",
  "Paid Social": "Marketing - Social Media",
  Referral: "Marketing - Referral",
  "Shopify - Web (not captured in GA)": "Shopify - Other",
  "Shopify- API integration": "Shopify - API integration",
  SMS: "Marketing - SMS",
  "Shopify - Draft Order": "Shopify - Draft Order",
  "Shopify - Recharge": "Shopify - Recharge",
  "Shopify - POS": "Shopify - POS",
};
